import { ChevronRight, ShieldAlert, ShieldCheck } from "lucide-react";

import { Panel } from "@/design-system/Panel";
import { StatusBadge } from "@/design-system/StatusBadge";
import type { ReleaseStatusResponse } from "@/shared/api";
import { componentReleaseIdentity } from "@/shared/release/componentRelease";
import {
  COMPATIBILITY,
  HEALTH,
  RUNTIME_IDENTITY,
  RELEASE_STATUS,
  provenance,
  reason,
  releaseClass,
  releaseVerdict,
  shortSha,
  type Verdict,
} from "@/shared/release/releaseLanguage";
import { useReleaseStatus } from "@/shared/release/useReleaseStatus";

function dateTime(value: string | null): string {
  return value === null
    ? "—"
    : new Date(value).toLocaleString("ru-RU", { dateStyle: "medium", timeStyle: "short" });
}

type Fact = {
  readonly label: string;
  readonly value: string;
  /** The exact evidence behind a readable value: a full SHA or a reason code. */
  readonly exact?: string | undefined;
  readonly verdict?: Verdict | undefined;
  readonly code?: string | undefined;
  readonly note?: string | undefined;
  /** Identifiers are compared character by character; words are read. */
  readonly mono?: boolean | undefined;
};

function FactList({ facts }: { readonly facts: readonly Fact[] }): React.JSX.Element {
  return (
    <dl className="grid min-w-0 grid-cols-[repeat(auto-fit,minmax(14rem,1fr))] gap-x-5 gap-y-3">
      {facts.map((fact) => (
        <div key={fact.label} className="min-w-0">
          <dt className="text-label-caps text-text-muted">{fact.label}</dt>
          <dd className="mt-1 min-w-0" title={fact.exact} data-code={fact.code}>
            {fact.verdict === undefined ? (
              <span
                className={
                  fact.mono === true
                    ? "break-all font-mono text-data-mono text-text-primary"
                    : "text-body-main text-text-primary"
                }
              >
                {fact.value}
              </span>
            ) : (
              <StatusBadge tone={fact.verdict.tone}>{fact.verdict.text}</StatusBadge>
            )}
          </dd>
          {fact.note !== undefined && (
            <p className="mt-0.5 text-helper-sm text-text-muted">{fact.note}</p>
          )}
        </div>
      ))}
    </dl>
  );
}

/** Which code is running: the four identities a release consists of. */
export function versionFacts(release: ReleaseStatusResponse): readonly Fact[] {
  const component = componentReleaseIdentity();
  const web = release.web_sha ?? component?.webSha ?? null;
  return [
    { label: "Релиз", value: shortSha(release.production_sha), exact: release.production_sha, mono: true },
    { label: "Web", value: shortSha(web), exact: web ?? undefined, mono: true },
    { label: "API", value: shortSha(release.api_sha), exact: release.api_sha, mono: true },
    {
      label: "Сборка",
      value: release.build_identity === null ? "—" : release.build_identity.slice(0, 12),
      exact: release.build_identity ?? undefined,
      mono: true,
    },
    { label: "Схема базы данных", value: release.migration_head, mono: true },
  ];
}

/** Whether the pieces agree with each other and with the release. */
export function checkFacts(release: ReleaseStatusResponse): readonly Fact[] {
  return [
    {
      label: "Статус релиза",
      value: release.status,
      verdict: RELEASE_STATUS[release.status],
      code: release.status,
    },
    {
      label: "Web и API",
      value: release.component_compatibility,
      verdict: COMPATIBILITY[release.component_compatibility],
      code: release.component_compatibility,
    },
    {
      label: "Торговый runtime",
      value: release.runtime_identity_status,
      verdict: RUNTIME_IDENTITY[release.runtime_identity_status],
      code: release.runtime_identity_reason,
      exact: release.runtime_sha ?? release.runtime_observed_sha ?? undefined,
      note: reason(release.runtime_identity_reason),
    },
    {
      label: "Совместимость runtime",
      value: release.runtime_compatibility,
      verdict: COMPATIBILITY[release.runtime_compatibility],
      code: release.runtime_compatibility_reason ?? undefined,
      note: reason(release.runtime_compatibility_reason),
    },
    {
      label: "Сервис",
      value: release.health_status,
      verdict: HEALTH[release.health_status],
      code: release.health_status,
    },
  ];
}

/** Where this release came from and where it would go back to. */
export function originFacts(release: ReleaseStatusResponse): readonly Fact[] {
  return [
    { label: "Тип релиза", value: releaseClass(release.release_class), exact: release.release_class ?? undefined },
    { label: "Установлен", value: dateTime(release.runtime_recorded_at ?? release.deployed_at) },
    {
      label: "Основание установки",
      value: provenance(release.runtime_provenance),
      exact: release.runtime_provenance ?? undefined,
    },
    {
      label: "Откат на версию",
      value: shortSha(release.rollback_target),
      exact: release.rollback_target ?? undefined,
      mono: true,
    },
    {
      label: "Main в репозитории",
      value: shortSha(release.canonical_main_sha),
      exact: release.canonical_main_sha ?? undefined,
      mono: true,
      note: release.main_observed_at === null ? undefined : `проверено ${dateTime(release.main_observed_at)}`,
    },
  ];
}

/** Whether every check agrees, said once in words the owner acts on. */
function summary(release: ReleaseStatusResponse): string {
  const checks = checkFacts(release);
  if (checks.every((fact) => fact.verdict?.tone === "ready")) {
    return "Всё в порядке: установленная версия проверена, её части совместимы, сервис работает.";
  }
  // A value no verdict maps to is unconfirmed, so it is named as failing too.
  const failing = checks.filter((fact) => fact.verdict?.tone !== "ready");
  return `Требует проверки: ${failing.map((fact) => fact.label.toLowerCase()).join(", ")}. Подробности ниже.`;
}

/*
 * The release record says nothing about whether accounts trade: the web
 * release holds no exchange authority by design. It used to print "trading
 * off" here, which read as the bot being stopped while it traded (2026-10-13).
 */
export function ReleaseFacts({ release }: { readonly release: ReleaseStatusResponse }): React.JSX.Element {
  const verdict = releaseVerdict(release);
  const fine = verdict.tone === "ready";
  return (
    <div className="space-y-2">
      <Panel>
        <div className="flex flex-col gap-4" data-testid="release-verdict" data-status={release.status}>
          <div className="flex items-start gap-3">
            {fine
              ? <ShieldCheck aria-hidden="true" className="mt-0.5 size-6 shrink-0 text-status-positive" />
              : <ShieldAlert aria-hidden="true" className="mt-0.5 size-6 shrink-0 text-status-warning" />}
            <div className="flex flex-col gap-1">
              <span className="text-body-lg font-semibold text-text-primary">{verdict.text}</span>
              <span className="text-body-main text-text-secondary">{summary(release)}</span>
            </div>
          </div>
          <dl className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div>
              <dt className="text-helper-sm text-text-muted">Работает версия</dt>
              <dd className="font-mono text-data-mono text-text-primary" title={release.production_sha}>{shortSha(release.production_sha)}</dd>
            </div>
            <div>
              <dt className="text-helper-sm text-text-muted">Установлена</dt>
              <dd className="text-body-main text-text-primary">{dateTime(release.runtime_recorded_at ?? release.deployed_at)}</dd>
            </div>
            <div>
              <dt className="text-helper-sm text-text-muted">Если что-то пойдёт не так — откат на</dt>
              <dd className="font-mono text-data-mono text-text-primary" title={release.rollback_target ?? undefined}>{shortSha(release.rollback_target)}</dd>
            </div>
          </dl>
        </div>
      </Panel>
      <details className="group rounded-lg border border-border-subtle" open={!fine}>
        <summary className="flex cursor-pointer items-center gap-2 px-4 py-3 text-body-main text-text-secondary transition-colors duration-150 hover:text-text-primary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring">
          <ChevronRight aria-hidden="true" className="size-4 transition-transform duration-150 group-open:rotate-90 motion-reduce:transition-none" />
          Технические подробности
        </summary>
        <div className="grid grid-cols-1 gap-2 p-2 pt-0 xl:grid-cols-3">
          <Panel title="Версия">
            <FactList facts={versionFacts(release)} />
          </Panel>
          <Panel title="Проверки">
            <FactList facts={checkFacts(release)} />
          </Panel>
          <Panel title="Происхождение и откат">
            <FactList facts={originFacts(release)} />
          </Panel>
        </div>
      </details>
    </div>
  );
}

export function ProductionRelease(): React.JSX.Element {
  const state = useReleaseStatus();

  if (state.status !== "ready") {
    return (
      <Panel title="Релиз">
        <p className="text-body-main text-text-muted">
          {state.status === "loading" ? "Читаю состояние релиза…" : "Состояние релиза недоступно"}
        </p>
      </Panel>
    );
  }

  return (
    <section data-testid="production-release" aria-label="Релиз в Production">
      <ReleaseFacts release={state.release} />
    </section>
  );
}
